import React, { useEffect, useRef, useState } from 'react'
import './managing-list.styles.scss'

import { useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom';
import ThreeDotsLoader from '@/components/loader/three-dots-loader.component';
import ImageContainer from '@/components/SHARED/image-container/image-container.component';
import { format } from 'date-fns';
import { countsTowardGameLimit, membershipRestrictions, testMemberRestrictions } from '@/utils/membershipRestrictions';
import UpgradeRequiredModal from '@/components/SHARED/upgrade-required-modal/upgrade-required-modal.component';
import { properCase } from '@/utils/properCase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArchive, faEllipsisV } from '@fortawesome/free-solid-svg-icons';
import GameManageDropmenu from './game-manage-dropmenu.component';
import { ModalAlert, ModalConfirm } from '@/components/modal/modal.component';
import { UseCloudFunction } from '@/utils/firebase.utils';
import FullPageLoader from '@/components/loader/full-page-loader.component';

const ManagingList = ({
  managingList,
  archivedList
}) => {

  const membership = useSelector(state => state.userData.membership);
  const screenSize = useSelector(state => state.contentData.screenSize);
  const userProfile = useSelector(state => state.userData.userProfile);
  const [listToDisplay, setListToDisplay] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState(null);
  const [showDropMenu, setShowDropMenu] = useState(null);
  const [alertMessage, setAlertMessage] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fullLoading, setFullLoading] = useState(false);
  const approveRef = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 700)
  }, [])

  useEffect(() => {
    if (!managingList || !membership || !membershipRestrictions) return;
    let displayArr = [];
    let index = 1;
    Object.values(managingList)
      .sort((a, b) => a.ts < b.ts ? 1 : -1)
      .map(game => {
        // a game the user only collaborates on is governed by the owner's plan,
        // which this list has no way to look up — the owner's tier is only
        // resolved inside /manage/:gamePath. so it neither consumes a slot nor
        // gets judged against the viewer's own player/element caps here; the
        // manage page re-checks it against the plan that actually governs it.
        if (!countsTowardGameLimit(game)) {
          displayArr.push({ ...game });
          return;
        }
        const newGame = testMemberRestrictions({ 'gameData': game, 'membership': membership, 'gameIndex': index });
        if (!newGame.disabled) {
          index++;
        }
        // if (newGame.playerCount && newGame.createdTeams) {
        //     newGame.playersOnlyCount = newGame.playerCount - newGame.createdTeams;
        // }
        displayArr.push(newGame);
      })
    // console.log(displayArr);
    setListToDisplay(displayArr);
  }, [managingList, membership, membershipRestrictions])

  function promptUpgrade(g) {
    console.log(g);
    const planRest = membershipRestrictions[membership.membership];
    let message = '';
    if (g.disabledReason.includes('games')) {
      message += `<p>While using the ${planRest.title}, you are only able to have ${planRest.gameCount} active Games.</p>`
    }
    if (g.disabledReason.includes('players')) {
      message += `<p>While using the ${planRest.title}, you are only able to have ${planRest.playerCount} active Players per game.</p>`
    }
    if (g.disabledReason.includes('elements')) {
      message += `<p>While using the ${planRest.title}, you are only able to have ${planRest.elementCount} active Game Elements per game.</p>`
    }
    setUpgradeMessage(message);
    setShowUpgradeModal(true);

  }

  async function makeGameCopy(gameId) {
    setConfirmMessage('Are you sure you want to make a copy of this game?');
    approveRef.current = () => {
      continueGameCopy();
    }

    async function continueGameCopy() {
      setShowDropMenu(null);
      setFullLoading(true);
      const res = await UseCloudFunction(
        'duplicateOwnerGame',
        {
          'gameId': gameId,
          'appId': userProfile.appId,
          'userProfile': userProfile,
        }
      );
      console.log(res);
      setFullLoading(false);
    }
  }

  async function markAsArchived(gameData) {
    setConfirmMessage('Are you sure you want to archive this game?');
    approveRef.current = () => {
      continueGameArchive();
    }

    async function continueGameArchive() {
      setShowDropMenu(null);
      setFullLoading(true);
      const now = new Date().getTime();
      let tempGameData = { ...gameData };
      tempGameData.status = 'archived';
      tempGameData.ts = now;
      tempGameData.ts_archived = now;
      const res = await UseCloudFunction(
        'saveGame',
        {
          'gameObj': tempGameData,
          'profile': userProfile,
          'action': 'archived',
          'userProfile': userProfile,
        }
      );
      console.log(res);
      setFullLoading(false);
    }
  }

  return (
    <div className={`managing-list ${screenSize}`}>
      <div className='list-grid'>
        {
          (loading)
            ?
            <div className='center'><ThreeDotsLoader /></div>
            :
            (listToDisplay && listToDisplay.length > 0)
              ?
              listToDisplay
                .map(g =>
                  g.disabled
                    ?
                    (
                      <div key={g.gameId}
                        className={`game-row upgrade g-list-item ${screenSize}`}>
                        <button
                          className={`g-button game-card`}
                          onClick={() => promptUpgrade(g)}
                        >
                          <div className='name-and-icon'>
                            <ImageContainer
                              src={g.icon}
                              className={`game-icon disabled ${g.border_icon}-border`}
                              style={{ 'border': `2px solid ${g.color}` }} />

                            <div className='game-name disabled'>
                              {g.gameName}<br />
                              <span className='meta'>Role: {properCase(g.role)}</span>
                            </div>
                          </div>

                        </button>
                        <div>Upgrade to access</div>
                        <div className="g-space-1"></div>
                      </div>
                    )
                    :
                    (
                      <div
                        key={g.gameId}
                        className={`game-row g-list-item ${screenSize}`}>
                        <Link to={`/manage/${g.path}`} className='game-link'>
                          <button className='g-button game-card '>
                            <div className='name-and-icon'>
                              <ImageContainer
                                src={g.icon}
                                className={`game-icon ${g.border_icon}-border`}
                                style={{ 'border': `2px solid ${g.color}` }} />
                              <div className='game-name'>
                                {g.gameName}<br />
                                <span className='meta'>Role: {properCase(g.role)}</span>
                              </div>
                            </div>
                          </button>
                        </Link>

                        <div className='game-data-container'>
                          <div className='game-data'>
                            <div className={`meta ${screenSize === 'mobile'
                              ? 'info-inline'
                              : 'info-stacked'
                              }`}>
                              <div><b>Last Activity:</b></div>
                              <div>{format(new Date(g.ts), 'P')}</div>
                            </div>
                            <div className={`meta ${screenSize === 'mobile'
                              ? 'info-inline'
                              : 'info-stacked'
                              }`}>
                              <div><b>Join Code:</b></div>
                              <div>{g.joinCode}</div>
                            </div>
                            <div className='meta'>
                              <div><b>Players: </b></div>
                              <div>{g.playersOnlyCount ?? g.playerCount ?? '0'}</div>
                            </div>
                            {
                              (screenSize !== 'mobile') &&
                              <div className='menu-button-div'>
                                <button
                                  className='g-button small-btn'
                                  onClick={() => setShowDropMenu(
                                    showDropMenu === g.gameId
                                      ? null
                                      : g.gameId
                                  )}>
                                  <FontAwesomeIcon icon={faEllipsisV} />
                                </button>
                                <GameManageDropmenu
                                  active={showDropMenu === g.gameId}
                                  makeGameCopy={() => makeGameCopy(g.gameId)}
                                  markAsArchived={() => markAsArchived(g)} />
                              </div>
                            }
                          </div>
                          {
                            (screenSize === 'mobile') &&
                            <div className='menu-button-div'>
                              <button
                                className='g-button small-btn'
                                onClick={() => setShowDropMenu(
                                  showDropMenu === g.gameId
                                    ? null
                                    : g.gameId
                                )}>
                                <FontAwesomeIcon icon={faEllipsisV} />
                              </button>
                              <GameManageDropmenu
                                active={showDropMenu === g.gameId}
                                makeGameCopy={() => makeGameCopy(g.gameId)}
                                markAsArchived={() => markAsArchived(g)} />
                            </div>
                          }
                        </div>
                        {
                          (screenSize === 'mobile') &&
                          <div style={{ 'width': '100%' }}>
                            <div className="g-space-0-5"></div>
                            {/* <hr className='no-margin' />
                                    <div className="g-space-0-5"></div> */}
                          </div>
                        }
                      </div>
                    )
                )
              :
              <div className='center-col'>
                <h3>No games yet...</h3>
              </div>
        }
        {/* <div className='g-card'>
                    <div className='video-image'>

                    </div>
                </div> */}
        <div className="g-space-1"></div>
        <div
          id="archive-button"
          className={Object.values(archivedList).length < 1 ? 'disabled' : ''}>
          <Link to='../archive'>
            <button className='g-button med-btn'>
              <FontAwesomeIcon icon={faArchive} />
              View Archived Games
            </button>
          </Link>
        </div>
      </div>
      <UpgradeRequiredModal
        show={showUpgradeModal}
        cancel={() => setShowUpgradeModal(false)}
        closeButton={true}
        message={upgradeMessage} />
      <ModalAlert
        show={alertMessage}
        cancel={() => setAlertMessage(null)}
        message={alertMessage} />
      <ModalConfirm
        show={confirmMessage}
        cancel={() => setConfirmMessage(null)}
        message={confirmMessage}
        onApprove={approveRef.current} />
      <FullPageLoader show={fullLoading} />
    </div>
  )
}

export default ManagingList